import { reactive, toRefs } from 'vue';
import { useUserStore, useGrayscaleStore } from '@/store';
// import axios from 'axios';
import OSS from 'ali-oss'; // 引入ali-oss
import { publicService } from '@/api/';
import { Message } from '@arco-design/web-vue';

import i18n from '@/locales/index';
export default function useXlUpload() {
  const data = reactive({
    setOssConfig: {},
    setOssClient: null,
    partSize: 1024 * 1024 * 20,
    isOssReady: false, // 新增：标记OSS是否初始化完成
    accepts: '.jpg,.jpeg,.png,.zip,.gif,.webp,.docx,.docm,.doc,.dot,.xlsx,.xls,.txt,.pptx,.ppt,.pdf,.key,.pages,.bmp,.tiff,.ai,.cdr,.eps,.ico,.svg,audio/*,video/*',
    filePath: {}
  });
  const isFeiShuApp = window.h5sdk;
  // 获取文件地址(0:link,1:img)
  const setUrl = (fileItem, type = 1) => {
    // 1. 基础校验：若url为空或非字符串，直接返回空（或原始值）
    const url = fileItem?.signedUrl || fileItem?.response?.data?.[0]?.url || fileItem?.url;
    if (!url || typeof url !== 'string') {
      //   console.warn('无效的URL参数:', url);
      return type == 0 ? 'javascript:void(0)' : '';
    }
    return url;
  };
  // 2. 校验URL是否有效
  const checkInvalidUrl = (fileItem) => {
    const url = fileItem?.signedUrl || fileItem?.response?.data?.[0]?.url || fileItem?.url;
    if (!url || typeof url !== 'string') {
      Message.warning('该文件无有效链接');
      return false;
    }
    return true;
  };

  const { userInfo } = useUserStore(); //用户信息
  // "accessKeyId" 临时AccessKeyId
  //"accessKeySecret"临时AccessKeySecret
  //"securityToken" STS临时令牌
  //"region":OSS区域
  //"bucket": OSS存储桶名称
  // "endpoint" OSS访问端点
  // 获取OSS配置和基础路径
  const getOssConfig = async () => {
    try {
      // 从后端获取OSS配置信息（包含临时密钥等）
      const res = await publicService.getOssBasePath();
      if (!res.error) {
        data.setOssConfig = res.result;
        // 初始化OSS客户端
        data.setOssClient = new OSS({
          region: data.setOssConfig.region,
          accessKeyId: data.setOssConfig.accessKeyId,
          accessKeySecret: data.setOssConfig.accessKeySecret,
          stsToken: data.setOssConfig.securityToken,
          bucket: data.setOssConfig.bucket,
          endpoint: data.setOssConfig.endpoint,
          secure: window.location.protocol == 'http:' ? false : true //本地和https都需要配置
        });
        data.isOssReady = true; // 标记初始化完成
      } else {
        // Message.error('获取上传配置失败');
        data.isOssReady = false;
      }
    } catch (error) {
      console.error('上传服务初始化失败:', error);
      data.isOssReady = false;
    }
  };

  // 文件名去除特殊字符
  const specialChars = [' ', '!', '#', '$', '%', '&', '"', '(', ')', '*', '+', ',', '/', ':', ';', '=', '?', '@', '[', ']'];
  const removeSpecialChars = (fileName) => {
    if (!fileName) return '';

    // 分离文件名和扩展名
    const n = fileName.lastIndexOf('.');
    const namePart = n > -1 ? fileName.substring(0, n) : fileName;
    const extPart = n > -1 ? fileName.substring(n) : '';

    // 使用正则表达式一次性替换所有特殊字符
    const regex = new RegExp(`[${specialChars.map((char) => `\\${char}`).join('')}]`, 'g');
    const cleanedName = namePart.replace(regex, '');

    return cleanedName + '_' + new Date().getTime() + extPart;
  };
  //生成文件路径
  const getFilePath = (fileItem) => {
    const orgid = userInfo?.orgId || '3';
    const userId = userInfo?.id || '0';
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    const updateFileName = removeSpecialChars(fileItem.name);
    const objectPath = `${data.setOssConfig.path}${orgid}/${date}/${userId}/${updateFileName}`;
    return objectPath;
  };
  // 上传文件
  const uploadFile = (fileItem) => {
    return new Promise((resolve, reject) => {
      // 大于20M 分片
      const getSize = fileItem.file.size;
      if (getSize > data.partSize) {
        data.setOssClient
          .multipartUpload(data.filePath[fileItem.uid].url, fileItem.file, {
            partSize: data.partSize, // 20MB分片(默认值为1 MB，最小值为100 KB。)
            parallel: 3, // 并发上传数
            retry: 3, // 重试次数
            timeout: 10 * 60 * 1000 // 10分钟
          })
          .then(resolve)
          .catch(reject);
      } else {
        data.setOssClient
          .put(data.filePath[fileItem.uid].url, fileItem.file, {
            mime: fileItem.type === 'text/plain' ? 'text/plain;charset=utf-8' : fileItem.type,
            timeout: 10 * 60 * 1000 // 10分钟
          })
          .then(resolve)
          .catch(reject);
      }
    });
  };
  //自定义上传请求(非oss上传)
  const customNotOssRequest = async (fileItem) => {
    // 想要设置的新文件名
    const newFileName = removeSpecialChars(fileItem.name);

    // 创建新的File对象，保持原文件内容，只修改文件名
    const newFile = new File([fileItem.file], newFileName, {
      type: fileItem.file.type,
      lastModified: fileItem.file.lastModified
    });

    const formData = new FormData();
    formData.append('file', newFile);
    try {
      const res = await publicService.uploadFile(formData);

      if (!res.error) {
        return res;
      }
      throw new Error('上传失败');
    } catch (error) {
      console.error('上传失败:', error);
      throw error; // 抛出错误让调用方处理
    }
  };
  // 自定义上传请求
  const customRequest = async (fileItem) => {
    const { grayList } = useGrayscaleStore();
    if (grayList?.['FileStorage:minioOss']) {
      //走后端上传逻辑
      //非oss上传
      return customNotOssRequest(fileItem);
    }
    // oss未初始化完成
    if (!data.isOssReady || !data.setOssClient) {
      const err = new Error('OSS客户端未初始化完成');
      throw new Error(err);
    }
    data.filePath[fileItem.uid] = {
      url: getFilePath(fileItem)
    };
    try {
      const result = await uploadFile(fileItem);

      const response = result?.res || null;
      if (response?.status === 200) {
        return result;
      }
      throw new Error(`上传失败，状态码: ${response?.status}`);
    } catch (error) {
      console.error('上传失败:', error);
      throw error; // 抛出错误让调用方处理
    }
  };
  //获取文件类型
  const getFileSuffix = (name) => {
    const n = name?.lastIndexOf('.');
    const ext = name?.substring(n + 1).toLowerCase();
    return ext;
  };
  const createFileItem = (file) => {
    // 创建文件对象，符合组件要求的格式
    const fileItem = {
      uid: `paste_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // 生成唯一ID
      name: file.name,
      file: file,
      status: 'uploading', // 初始状态
      size: file.size,
      type: file.type
    };
    return fileItem;
  };
  const getDraggedFiles = (clipboardData) => {
    if (!clipboardData) {
      Message.warning('浏览器不支持拖拽上传');
      return false;
    }
    const items = clipboardData.items; // 拖拽的所有项目
    const draggedFiles = []; // 存储筛选后的图片文件

    // 2. 筛选文件类型（只保留图片文件，可选：也可支持其他类型）
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // 筛选条件：① 是文件类型 ② 是图片（MIME 类型以 image/ 开头）
      if (item.kind === 'file') {
        const file = item.getAsFile(); // 将 item 转为 File 对象
        if (file) {
          draggedFiles.push(file); // 加入图片文件列表
        }
      }
    }
    if (draggedFiles.length === 0) return false;
    return draggedFiles;
  };
  /**
   * 判断文件URL是否属于指定类型
   * @param {string} name - 文件名
   * @param {string} type - 目标文件类型
   * @returns {boolean} 是否匹配指定类型
   */
  const getFileType = (name, type) => {
    const typeObj = {
      img: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'],
      video: ['mp4'],
      pdf: ['pdf'],
      ppt: ['ppt', 'pptx'],
      xlsx: ['xls', 'xlsx'],
      zip: ['zip', 'rar'],
      docs: ['doc', 'docx'],
      unableImg: ['tiff', 'ai', 'cdr', 'eps'], //无法预览的图片类型
      unableVideo: ['m2v', 'mkv', 'avi'] //无法预览的视频类型
    };
    if (name) {
      const n = name.lastIndexOf('.');
      const ext = name.substring(n + 1).toLowerCase();
      return typeObj[type]?.includes(ext);
    }
    return false;
  };
  const getDownloadFile = async (fileUrl, fileName) => {
    try {
      const response = await publicService.downloadFile(fileUrl);
      if (!response.error) {
        // 3. 生成本地临时 URL
        const tempUrl = URL.createObjectURL(response);

        // 4. 创建隐藏 <a> 标签，触发下载
        const a = document.createElement('a');
        a.href = tempUrl;
        a.download = fileName; // 自定义文件名（必须带后缀）
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();

        // 5. 清理临时资源（避免内存泄漏）
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(tempUrl);
          // 浏览器环境： 文件已下载完成，可通过浏览器下载文件夹查看
          // 飞书环境： 文件已下载完成，可通过飞书左下角近期下载文件夹查看
          Message.success(isFeiShuApp ? i18n.global.t('文件已下载完成，可通过飞书左下角近期下载文件夹查看') : i18n.global.t('文件已下载完成，可通过浏览器下载文件夹查看'));
        }, 500);
      } else {
        Message.error(i18n.global.t('获取文件失败，请重试！'));
      }
    } catch (error) {
      console.error('下载失败：', error);
      Message.error(i18n.global.t('下载失败，请重试！'));
    }
  };
  // 通用下载函数：支持跨域图片/PDF/视频
  const downloadFile = async (fileUrl, fileName) => {
    try {
      const response = await publicService.getPresignedUrl({ url: fileUrl });

      if (!response.error && response?.result) {
        getDownloadFile(response.result, fileName);
      } else {
        Message.error(i18n.global.t('获取文件失败，请重试！'));
      }
    } catch (error) {
      console.error('下载失败：', error);
      Message.error(i18n.global.t('下载失败，请重试！'));
    }
  };
  // 获取签名链接
  const getSignedUrl = async (fileUrl) => {
    let signedUrl = null; //oss上传签名url

    try {
      // 确保OSS客户端已初始化
      await getOssConfig();

      if (data.setOssClient) {
        // 使用await等待signatureUrl Promise完成
        signedUrl = await data.setOssClient.signatureUrl(fileUrl, {
          expires: 3600
        });
      }
    } catch (error) {
      console.error('获取签名URL失败：', error);
      // 可以根据需要添加错误处理逻辑，如Message提示
    }

    return signedUrl;
  };

  return {
    uploadFile,
    customRequest,
    setUrl,
    checkInvalidUrl,
    getOssConfig,
    getFileType,
    getFileSuffix,
    createFileItem,
    getDraggedFiles,
    downloadFile,
    getSignedUrl,
    getDownloadFile,
    ...toRefs(data)
  };
}
